import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/atoms/skeleton";
import type { ReactNode } from "react";

/**
 * The wait for Stripe's card fields, at their real geometry (spec skeleton 1.4).
 *
 * The PaymentElement takes a moment to mount and paints nothing at all while it does, so the
 * sheet had a 240 blank band between Name and the totals: not a wait, just a hole. Stripe's own
 * `loader` is no help — it paints on Stripe's ground, not on our paper.
 *
 * Three groups at the exact shape the element resolves to: card number, expiry beside the
 * security code, and country. Same label height, same 48 wells, same 16 between them, so the
 * real fields land where the blocks stood and nothing moves.
 */

/** Label 13 + 8 gap + the 48 well, three groups, 16 between: the band the element will fill. */
export const CARD_FIELDS_HEIGHT = 3 * (13 + 8 + 48) + 2 * 16;

export function CardFieldsSkeleton() {
  return (
    <div className="gap-gk-4 flex flex-col" aria-hidden="true">
      <Field width="34%" />
      <div className="gap-gk-4 grid grid-cols-2">
        <Field width="52%" />
        <Field width="44%" />
      </div>
      <Field width="26%" />
    </div>
  );
}

function Field({ width }: { width: string }) {
  return (
    <div className="gap-gk-2 flex flex-col">
      <Skeleton style={{ width, height: 13 }} />
      <Skeleton style={{ height: 48 }} />
    </div>
  );
}

/**
 * The band the card fields land in.
 *
 * `ready` alone is not the answer, and that is the whole reason this component exists: Stripe
 * fires `ready` as soon as the element is MOUNTED, seconds before the iframe has painted
 * anything, so a skeleton keyed on `ready` lifted at 1.5 s and left a 240 blank band under Name
 * until about 5 s. Measured at 1440: skeleton gone at 2.5 s, fields drawn at 6 s.
 *
 * So the band watches the element instead. It holds the skeleton until Stripe has said ready AND
 * the element has actually laid out to something the height of a form, and it reserves the band
 * until then so nothing below it moves when the fields arrive.
 */
export function CardFieldsBand({
  ready,
  children,
}: {
  ready: boolean;
  children: ReactNode;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot || drawn) return;
    const check = () => {
      if (slot.getBoundingClientRect().height >= PAINTED_HEIGHT) setDrawn(true);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(slot);
    return () => observer.disconnect();
  }, [drawn]);

  const settled = ready && drawn;

  return (
    <div
      className="relative"
      style={{ minHeight: settled ? undefined : CARD_FIELDS_HEIGHT }}
    >
      {!settled && (
        <div className="bg-surface-paper absolute inset-0 z-10">
          <CardFieldsSkeleton />
        </div>
      )}
      <div ref={slotRef}>{children}</div>
    </div>
  );
}

/** Enough of the band filled that the fields are really there and not just mounted. */
const PAINTED_HEIGHT = 120;
